import React, { useEffect, useState } from "react";
import { Result, Spin, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const { Text } = Typography;

// OAuth-style "Login with DMS" gate for the Edge360 onboarding page.
// The device sends the technician here with ?redirect_uri=<device page>&state=<rand>.
// If a valid DMS session exists, we redirect straight back with the access token in
// the URL fragment; otherwise we bounce through /login and resume.

// Only allow redirecting a token back to a private-LAN / localhost device page —
// never to an arbitrary internet host (prevents token exfiltration).
function isAllowedRedirect(uri) {
  try {
    const u = new URL(uri);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    const h = u.hostname;
    if (h === "localhost" || h === "127.0.0.1" || h === "::1") return true;
    if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
    if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
    if (/^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
    // mDNS .local hostnames (e.g. edge360-<id>.local) are LAN-only — the .local
    // TLD is reserved for multicast DNS and can never route to an internet host,
    // so allowing it preserves the no-exfiltration guarantee.
    if (/\.local$/i.test(h)) return true;
    const allow = process.env.REACT_APP_EDGE360_ONBOARD_HOST;
    if (allow && h === allow) return true;
    return false;
  } catch (e) {
    return false;
  }
}

export default function DeviceLogin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  const params = new URLSearchParams(window.location.search);
  const redirectUri = params.get("redirect_uri");
  const state = params.get("state") || "";

  useEffect(() => {
    if (loading) return;

    if (!redirectUri || !isAllowedRedirect(redirectUri)) {
      setError(
        "This onboarding link points to an untrusted address, so we won't return your sign-in there. The device page must use a local (LAN) address."
      );
      return;
    }

    const token = localStorage.getItem("accessToken");

    // Valid DMS session → hand the token back to the device page (in the fragment).
    if (token && user) {
      const sep = redirectUri.includes("#") ? "&" : "#";
      const target =
        `${redirectUri}${sep}access_token=${encodeURIComponent(token)}` +
        `&token_type=Bearer&state=${encodeURIComponent(state)}`;
      window.location.replace(target);
      return;
    }

    // No valid session → clear any stale token and send to the DMS login, then resume.
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    const here = `/device-login?redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
    navigate(`/login?return=${encodeURIComponent(here)}`, { replace: true });
  }, [loading, user, redirectUri, state, navigate]);

  if (error) {
    return <Result status="error" title="Can't authorize this device" subTitle={error} />;
  }

  return (
    <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
      <Spin size="large" />
      <Text type="secondary">Signing you in to the device…</Text>
    </div>
  );
}
